import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Title2, Body1, Body1Strong, Button, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import { useUser } from "../lib/user";
import { BrandLogo } from "../components/Brand";

interface EmbedResponse {
  embedUrl: string;
  expiresAt: string; // ISO-8601
  dashboardDisplayName: string;
  clientDisplayName: string;
}

const useStyles = makeStyles({
  chrome: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1
  },
  spacer: { flex: 1 },
  iframeWrap: { flex: 1, position: "relative" },
  iframe: { width: "100%", height: "100%", border: "none" },
  errorBox: { padding: tokens.spacingHorizontalXXL, textAlign: "center" }
});

const REFRESH_BUFFER_MS = 60_000; // refresh embed URL 1 min before expiry

export default function DashboardViewer() {
  const styles = useStyles();
  const { clientId, dashboardId } = useParams<{ clientId: string; dashboardId: string }>();
  const user = useUser();
  const [embed, setEmbed] = useState<EmbedResponse | "loading" | "unavailable" | "error">("loading");
  const refreshTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadEmbedUrl() {
      if (!clientId || !dashboardId) return;
      setEmbed("loading");
      try {
        const r = await fetch(`/api/embed?clientId=${encodeURIComponent(clientId)}&dashboardId=${encodeURIComponent(dashboardId)}`);
        if (r.status === 404) {
          // 404 covers both "no such dashboard" and "not entitled" — the API
          // deliberately doesn't distinguish them (no client-roster enumeration).
          if (!cancelled) setEmbed("unavailable");
          return;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as EmbedResponse;
        if (cancelled) return;
        setEmbed(data);
        scheduleRefresh(data.expiresAt);
      } catch {
        if (!cancelled) setEmbed("error");
      }
    }

    function scheduleRefresh(expiresAtIso: string) {
      if (refreshTimerRef.current !== null) window.clearTimeout(refreshTimerRef.current);
      const expiresAt = new Date(expiresAtIso).getTime();
      const delay = Math.max(expiresAt - Date.now() - REFRESH_BUFFER_MS, 10_000);
      refreshTimerRef.current = window.setTimeout(loadEmbedUrl, delay);
    }

    loadEmbedUrl();

    return () => {
      cancelled = true;
      if (refreshTimerRef.current !== null) window.clearTimeout(refreshTimerRef.current);
    };
  }, [clientId, dashboardId]);

  if (embed === "loading") {
    return (
      <div className={styles.errorBox}>
        <Spinner size="medium" label="Preparing dashboard…" />
      </div>
    );
  }
  if (embed === "unavailable") {
    return (
      <div className={styles.errorBox}>
        <Title2>This dashboard isn't available</Title2>
        <Body1>
          It may have been moved or removed, or you may not have access. Contact your
          AdQuire contact if you think this is a mistake.
        </Body1>
        <Button as="a" href="/" appearance="primary">Back to dashboards</Button>
      </div>
    );
  }
  if (embed === "error") {
    return (
      <div className={styles.errorBox}>
        <Title2>Couldn't load this dashboard</Title2>
        <Body1>The database may be waking up. Try refreshing in about a minute.</Body1>
        <Button as="a" href="/" appearance="primary">Back to dashboards</Button>
      </div>
    );
  }

  return (
    <>
      <div className={styles.chrome}>
        <Link to="/" state={{ showList: true }} aria-label="intnt Client Reporting — dashboards" style={{ display: "inline-flex" }}>
          <BrandLogo height={22} />
        </Link>
        <Link to="/" state={{ showList: true }} style={{ textDecoration: "none", color: tokens.colorNeutralForeground1 }}>
          ← Dashboards
        </Link>
        <Body1Strong>{embed.clientDisplayName} · {embed.dashboardDisplayName}</Body1Strong>
        <span className={styles.spacer} />
        {user && <Body1>{user.userDetails}</Body1>}
        <a href="/logout">Sign out</a>
      </div>
      <div className={styles.iframeWrap}>
        <iframe
          className={styles.iframe}
          src={embed.embedUrl}
          title={embed.dashboardDisplayName}
          allow="fullscreen"
        />
      </div>
    </>
  );
}
